import { useQuery } from "@tanstack/react-query"
import { Link, useParams } from "react-router-dom"
import { api } from "@/api.js"
import { Async } from "@/components/Async.js"
import { Notice, Section } from "@/components/Page.js"
import { DecisionBadge, DegradedFlag, ReasonBadge } from "@/components/Status.js"
import { Table, Td, Th, Tr } from "@/components/ui/table.js"
import { cn } from "@/lib/utils.js"
import { useQuestions } from "@/questions.js"
import { formatDate } from "./Entity.js"

const BAR: Record<string, string> = {
  ALLOW: "border-allow",
  PREPAYMENT: "border-prepay",
  REVIEW: "border-review",
}

/** One answer, completely. This is what gets pasted into a support ticket. */
export function DecisionDetailScreen() {
  const { decisionId = "" } = useParams()
  const { titleOf } = useQuestions()
  const state = useQuery({
    queryKey: ["decision", decisionId],
    queryFn: () => api.decision(decisionId),
  })

  return (
    <Async state={state}>
      {(detail) => (
        <div className="space-y-8">
          {/* The stamp: the verdict carries the file's colour on its spine. */}
          <header
            className={cn("border-l-[3px] py-1 pl-5", BAR[detail.decision] ?? "border-prepay")}
          >
            <p className="text-[13px] text-muted-foreground">
              Decyzja · {titleOf(detail.question)}
            </p>
            <div className="mt-1 flex flex-wrap items-center gap-4">
              <DecisionBadge decision={detail.decision} size="lg" />
              <DegradedFlag degraded={detail.degraded} />
            </div>
            <p className="mt-2 text-sm text-muted-foreground">
              <Link
                className="font-mono text-[13px] font-medium text-foreground no-underline hover:text-primary"
                to={`/entities/${detail.country}/${detail.taxId}`}
              >
                {detail.country}
                {detail.taxId}
              </Link>{" "}
              · {detail.apiClientId} · {formatDate(detail.evaluatedAt)} · konfiguracja v
              {detail.configVersion} · {detail.latencyMs} ms
            </p>
            <p className="mt-1 font-mono text-xs text-muted-foreground">{detail.decisionId}</p>
          </header>

          {detail.decisionBeforeOverride !== null && (
            <Notice tone="review">
              Człowiek podstawił tę odpowiedź. Bez odstępstwa reguły doszły do{" "}
              <span className="font-semibold">
                {detail.decisionBeforeOverride === "ALLOW" ? "odroczenia" : "przedpłaty"}
              </span>{" "}
              (odstępstwo <span className="font-mono text-[12.5px]">{detail.overrideId}</span>).
              Treść uzasadnienia jest wewnętrzna i nie trafia do konsumenta.
            </Notice>
          )}

          <Section
            title="Powody, w kolejności ewaluacji"
            hint="To, co reguła faktycznie zobaczyła — nie tylko kod."
          >
            <Table>
              <thead>
                <tr>
                  <Th>Kod</Th>
                  <Th>Reguła</Th>
                  <Th>Rodzaj</Th>
                  <Th>Co zobaczyła</Th>
                </tr>
              </thead>
              <tbody>
                {detail.reasons.length === 0 ? (
                  <Tr>
                    <Td colSpan={4} className="text-muted-foreground">
                      Bez zastrzeżeń — żadna reguła nic nie zgłosiła.
                    </Td>
                  </Tr>
                ) : (
                  detail.reasons.map((reason) => (
                    <Tr key={reason.seq}>
                      <Td>
                        <ReasonBadge code={reason.code} kind={reason.kind} />
                      </Td>
                      <Td className="font-mono text-[13px]">{reason.rule ?? "—"}</Td>
                      <Td>{reason.kind}</Td>
                      <Td className="font-mono text-[13px]">
                        {reason.observed === null ? "—" : JSON.stringify(reason.observed)}
                      </Td>
                    </Tr>
                  ))
                )}
              </tbody>
            </Table>
          </Section>

          <Section
            title="Wywołania providerów"
            hint="Źródło odpowiedzi i to, czy kosztowała limit."
          >
            <Table>
              <thead>
                <tr>
                  <Th>Provider</Th>
                  <Th>Źródło</Th>
                  <Th>Wynik</Th>
                  <Th className="text-right">Czas</Th>
                  <Th className="text-right">Limit</Th>
                </tr>
              </thead>
              <tbody>
                {detail.providerCalls.length === 0 ? (
                  <Tr>
                    <Td colSpan={5} className="text-muted-foreground">
                      Żadnego źródła nie pytano.
                    </Td>
                  </Tr>
                ) : (
                  detail.providerCalls.map((call) => (
                    <Tr key={call.providerId}>
                      <Td className="font-mono text-[13px]">{call.providerId}</Td>
                      <Td>{sourceLabel(call.source)}</Td>
                      <Td>{call.outcome}</Td>
                      <Td className="text-right font-mono text-[13px]">{call.latencyMs} ms</Td>
                      <Td className="text-right font-mono text-[13px]">{call.quotaSpent}</Td>
                    </Tr>
                  ))
                )}
              </tbody>
            </Table>
          </Section>
        </div>
      )}
    </Async>
  )
}

/**
 * The stored identifier, except for the one value that is not a source.
 *
 * `local`, `cache` and `api` are the strings in the audit log and are what
 * somebody carries into a ticket, so they are printed as they are stored.
 * `none` is the *absence* of a source — nothing was consulted, because the
 * source had no key, no session, or a reservation the ledger refused — and
 * printed raw under a column headed *Źródło* it says nothing at all.
 */
function sourceLabel(source: string): string {
  return source === "none" ? "nie pytano" : source
}
